import { Handshake, Pencil, UserPlus } from "lucide-react";
import {
  RecordContextProvider,
  ShowBase,
  useListContext,
  useRecordContext,
  useShowContext,
  useTranslate,
} from "ra-core";
import { Link, useMatch, useNavigate } from "react-router";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { contactFullName } from "../contacts/contactModel";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { formatDate } from "../misc/formatDate";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { ContractTypeName } from "../sponsorings/ContractTypeName";
import type { Company, Contact, Sponsoring } from "../types";
import { CompanyAside, CompanyInfo, CompanyRemarks } from "./CompanyAside";
import { CompanyAvatar } from "./CompanyAvatar";
import { CompanyDeleteButton } from "./CompanyDeleteButton";

export const CompanyShow = () => {
  const isMobile = useIsMobile();

  return (
    <ShowBase>
      {isMobile ? <CompanyShowContentMobile /> : <CompanyShowContent />}
    </ShowBase>
  );
};

const CompanyShowContent = () => {
  const { record, isPending } = useShowContext<Company>();
  const translate = useTranslate();
  const navigate = useNavigate();

  // The tab lives in the URL so a reload (or a back navigation) keeps it.
  const tabMatch = useMatch("/companies/:id/show/:tab");
  const currentTab = tabMatch?.params?.tab ?? "contacts";

  const handleTabChange = (value: string) => {
    if (value === currentTab) return;
    navigate(
      value === "contacts"
        ? `/companies/${record?.id}/show`
        : `/companies/${record?.id}/show/${value}`,
    );
  };

  if (isPending || !record) return null;

  return (
    <div className="mt-2 flex pb-2 gap-8">
      <div className="flex-1 min-w-0">
        <Card>
          <CardContent>
            <div className="flex items-center gap-3 mb-4">
              <CompanyAvatar />
              <h2 className="text-2xl font-bold tracking-tight">
                {record.name}
              </h2>
            </div>
            <Tabs value={currentTab} onValueChange={handleTabChange}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="contacts">
                  {translate("resources.companies.tabs.contacts")}
                  <span className="ml-1 text-muted-foreground">
                    ({record.nb_contacts ?? 0})
                  </span>
                </TabsTrigger>
                <TabsTrigger value="sponsorings">
                  {translate("resources.companies.tabs.sponsorings")}
                  <span className="ml-1 text-muted-foreground">
                    ({record.nb_sponsorings ?? 0})
                  </span>
                </TabsTrigger>
              </TabsList>
              <TabsContent value="contacts">
                <div className="flex flex-col gap-2">
                  <div className="flex flex-row justify-end mt-1">
                    <CreateRelatedContactButton />
                  </div>
                  <ContactsList />
                </div>
              </TabsContent>
              <TabsContent value="sponsorings">
                <div className="flex flex-col gap-2">
                  <div className="flex flex-row justify-end mt-1">
                    <CreateRelatedSponsoringButton />
                  </div>
                  <SponsoringsList />
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      <CompanyAside />
    </div>
  );
};

const CompanyShowContentMobile = () => {
  const translate = useTranslate();
  const { record, isPending } = useShowContext<Company>();
  if (isPending || !record) return null;

  return (
    <>
      <MobileHeader>
        <MobileBackButton to="/companies" />
        <div className="flex min-w-0 flex-1">
          <h1 className="truncate text-xl font-semibold text-brand-navy">
            {translate("resources.companies.forcedCaseName")}
          </h1>
        </div>
        <div className="flex shrink-0 flex-row items-center gap-1">
          <Button
            asChild
            type="button"
            variant="ghost"
            size="icon"
            className="rounded-full"
          >
            <Link
              to={`/companies/${record.id}`}
              aria-label={translate("resources.companies.action.edit")}
            >
              <Pencil className="size-5" />
            </Link>
          </Button>
          <CompanyDeleteButton iconOnly label="ra.action.delete" />
        </div>
      </MobileHeader>

      <MobileContent>
        <div className="flex items-center gap-3 mb-6">
          <CompanyAvatar />
          <h2 className="text-2xl font-bold">{record.name}</h2>
        </div>
        <CompanyInfo record={record} />
        <CompanyRemarks record={record} />
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-1">
            {translate("resources.companies.tabs.contacts")}
          </h3>
          <ContactsList />
          <CreateRelatedContactButton className="mt-2 h-11 w-full" />
        </div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-1">
            {translate("resources.companies.tabs.sponsorings")}
          </h3>
          <SponsoringsList />
          <CreateRelatedSponsoringButton className="mt-2 h-11 w-full" />
        </div>
      </MobileContent>
    </>
  );
};

const ContactsList = () => {
  const translate = useTranslate();
  return (
    <ReferenceManyField
      reference="contacts"
      target="company_id"
      sort={{ field: "last_name", order: "ASC" }}
      perPage={50}
      empty={
        <p className="text-sm text-muted-foreground py-2">
          {translate("resources.companies.no_contacts")}
        </p>
      }
    >
      <ContactsIterator />
    </ReferenceManyField>
  );
};

const ContactsIterator = () => {
  const { data: contacts, error, isPending } = useListContext<Contact>();
  if (isPending || error || !contacts) return null;

  return (
    <ul className="divide-y">
      {contacts.map((contact) => (
        <li key={contact.id}>
          <Link
            to={`/contacts/${contact.id}/show`}
            className="flex flex-col py-2 px-1 hover:bg-muted transition-colors no-underline"
          >
            <span className="text-sm font-medium">
              {contactFullName(contact)}
            </span>
            {contact.email ? (
              <span className="text-sm text-muted-foreground break-all">
                {contact.email}
              </span>
            ) : null}
          </Link>
        </li>
      ))}
    </ul>
  );
};

const SponsoringsList = () => {
  const translate = useTranslate();
  return (
    <ReferenceManyField
      reference="sponsorings"
      target="company_id"
      sort={{ field: "end_date", order: "ASC" }}
      perPage={50}
      empty={
        <p className="text-sm text-muted-foreground py-2">
          {translate("resources.companies.no_sponsorings")}
        </p>
      }
    >
      <SponsoringsIterator />
    </ReferenceManyField>
  );
};

const SponsoringsIterator = () => {
  const translate = useTranslate();
  const { sponsoringStages } = useConfigurationContext();
  const { data: sponsorings, error, isPending } = useListContext<Sponsoring>();
  if (isPending || error || !sponsorings) return null;

  const stageLabel = (stage: string) =>
    sponsoringStages.find((item) => item.value === stage)?.label ?? stage;

  return (
    <ul className="divide-y">
      {sponsorings.map((sponsoring) => (
        <RecordContextProvider key={sponsoring.id} value={sponsoring}>
          <li>
            <Link
              to={`/sponsorings/${sponsoring.id}/show`}
              className="flex flex-col py-2 px-1 hover:bg-muted transition-colors no-underline"
            >
              <span className="text-sm font-medium">
                <ContractTypeName />
              </span>
              <span className="text-sm text-muted-foreground">
                {stageLabel(sponsoring.stage)}
                {" · "}
                {sponsoring.end_date
                  ? `${translate("resources.sponsorings.fields.end_date")}: ${formatDate(
                      sponsoring.end_date,
                    )}`
                  : translate("resources.sponsorings.no_end_date")}
              </span>
            </Link>
          </li>
        </RecordContextProvider>
      ))}
    </ul>
  );
};

const CreateRelatedContactButton = ({ className }: { className?: string }) => {
  const translate = useTranslate();
  const company = useRecordContext<Company>();
  return (
    <Button
      variant="outline"
      asChild
      size="sm"
      className={cn("h-9", className)}
    >
      <Link
        to="/contacts/create"
        state={company ? { record: { company_id: company.id } } : undefined}
        className="flex items-center gap-2"
      >
        <UserPlus className="h-4 w-4" />
        {translate("resources.contacts.action.add")}
      </Link>
    </Button>
  );
};

const CreateRelatedSponsoringButton = ({
  className,
}: {
  className?: string;
}) => {
  const translate = useTranslate();
  const company = useRecordContext<Company>();
  return (
    <Button
      variant="outline"
      asChild
      size="sm"
      className={cn("h-9", className)}
    >
      <Link
        to="/sponsorings/create"
        state={company ? { record: { company_id: company.id } } : undefined}
        className="flex items-center gap-2"
      >
        <Handshake className="h-4 w-4" />
        {translate("resources.sponsorings.action.add")}
      </Link>
    </Button>
  );
};
